import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import DashboardLayoutMain from "../../../layout/dashboard/DashboardLayoutMain";
import "../../../styles/dashboard/transaction/transactionIndex.css";
import TitleBtnFragment from "../../../components/fragment/title-btn/TitleBtnFragment";
import {
  RazorBadgeTwo,
  RazorDateTime,
  RazorInputField,
  RazorNumberFormat,
  RazorTable,
  RazorTableRow,
  RazorTextTrim,
  RazorTwinValue,
} from "../../../components/razor-reuse";
import FilterReusableComp from "../../../components/FilterReusableComp";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/type";
import {
  getBridgeTransaction,
  getBridgeTransactionCsv,
} from "../../../redux/transaction";
import DateRangeComponent from "../../../components/fragment/date-range/DateRangeComponent";
import { useNavigate } from "react-router-dom";
import { ALL_ROUTES } from "../../../routes/AllRoutes";
import { systemRoles } from "../../../utils/roles";
import CustomModal, {
  useDialogToggle,
} from "../../../components/fragment/custom-modal";
import { Button } from "../../../components/custom/custom-button";

const PER_PAGE = 20;

const YEAR_OPTIONS = [2020, 2021, 2022, 2023, 2024, 2025, 2026].map((year) => ({
  label: String(year),
  value: year,
}));

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
].map((label, idx) => ({ label, value: idx + 1 }));

// Bridge's `state` values aren't a fixed enum we control, so bucket by
// keyword rather than an exact-match list that'd miss variants we haven't seen.
const getStateBadgeClass = (state?: string) => {
  if (!state) return "";
  if (
    state.includes("fail") ||
    state.includes("error") ||
    state.includes("return") ||
    state.includes("refund")
  )
    return "failed";
  if (state.includes("processed") || state.includes("completed"))
    return "success";
  return "pending";
};

const formatStateLabel = (state?: string) =>
  state ? state.replace(/_/g, " ") : "---";

const BridgeTransaction = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dialogRef, openDialog, closeDialog } = useDialogToggle();

  const { user } = useSelector((state: RootState) => state.auth);
  const { loading_bridge, bridge_transaction } = useSelector(
    (state: RootState) => state.transaction,
  );

  const headerList = [
    "Sender Name",
    "Reference",
    "Source",
    "Destination",
    "Amount",
    "State",
    "Date",
    "Receipt",
  ];

  const [dateRange, setDateRange] = useState("");
  // Bridge paginates by cursor (starting_after) rather than page number, so
  // we track the cursor for the visible page plus a back-stack of the ones
  // that came before it to support a "Previous" button.
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(
    undefined,
  );
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>(
    [],
  );

  const [exportYear, setExportYear] = useState<{
    label: string;
    value: number;
  } | null>(null);
  const [exportMonth, setExportMonth] = useState<{
    label: string;
    value: number;
  } | null>(null);

  const canExport = !!user?.permissions?.includes("generate_bridge_report");

  // useEffect to check if user has permission to view transactions
  useEffect(() => {
    if (
      !user?.permissions?.includes(systemRoles[11]) &&
      user?.permissions?.length > 0
    ) {
      navigate(ALL_ROUTES?.DASHBOARD_OVERVIEW);
    }
  }, [user?.permissions]);

  const resetPaging = () => {
    setCurrentCursor(undefined);
    setCursorHistory([]);
  };

  const obj = {
    "filter[date]": dateRange,
    limit: PER_PAGE,
    starting_after: currentCursor,
  };
  useEffect(() => {
    if (user?.permissions?.includes(systemRoles[11])) {
      dispatch(getBridgeTransaction(obj) as any);
    }
  }, [dateRange, currentCursor, dispatch, user?.permissions]);

  const rows = bridge_transaction?.data || [];
  const hasNext = rows.length >= PER_PAGE;
  const hasPrevious = cursorHistory.length > 0;

  const handleNext = () => {
    const lastId = rows[rows.length - 1]?.id;
    if (!lastId) return;
    setCursorHistory((prev) => [...prev, currentCursor]);
    setCurrentCursor(lastId);
  };

  const handlePrevious = () => {
    setCursorHistory((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      const previousCursor = next.pop();
      setCurrentCursor(previousCursor);
      return next;
    });
  };

  const closeExportDialog = () => {
    closeDialog();
    setExportYear(null);
    setExportMonth(null);
  };

  const handleConfirmExport = () => {
    if (!exportYear || !exportMonth) return;
    dispatch(
      getBridgeTransactionCsv({
        year: exportYear.value,
        month: exportMonth.value,
      }) as any,
    );
    closeExportDialog();
  };

  return (
    <>
      <DashboardLayoutMain
        loading={loading_bridge && rows.length < 1}
        title="Bridge Transactions"
      >
        <div className="transaction-index-wrap-box-smartsend">
          <FilterReusableComp
            hideSearch
            hideExport={!canExport}
            onExport={openDialog}
            moreComponents={
              <>
                <DateRangeComponent
                  onSet={(param) => {
                    setDateRange(param);
                    resetPaging();
                  }}
                  value={dateRange}
                  onCancel={() => {
                    setDateRange("");
                    resetPaging();
                  }}
                />
              </>
            }
          />
          {/* filter section end */}
          {/* table start */}
          <div className="table-section-box">
            <RazorTable headerList={headerList}>
              {rows.map((chi: any, idx: any) => {
                return (
                  <RazorTableRow
                    key={chi?.id || idx}
                    one={chi?.customer?.name || "------"}
                    two={RazorTextTrim(chi?.client_reference_id || "----", 22)}
                    three={
                      <RazorTwinValue
                        one={chi?.source?.currency?.toUpperCase() || "---"}
                        two={chi?.source?.payment_rail || ""}
                      />
                    }
                    four={
                      <RazorTwinValue
                        one={chi?.destination?.currency?.toUpperCase() || "---"}
                        two={chi?.destination?.payment_rail || ""}
                      />
                    }
                    five={RazorNumberFormat(chi?.amount, {
                      hideSymbol: false,
                      symbol: chi?.currency,
                    })}
                    six={
                      <RazorBadgeTwo
                        className={getStateBadgeClass(chi?.state)}
                        text={formatStateLabel(chi?.state)}
                      />
                    }
                    loading={loading_bridge}
                    seven={<RazorDateTime date={chi?.created_at} />}
                    eight={
                      chi?.receipt?.url ? (
                        <a
                          href={chi.receipt.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#734792] font-medium"
                        >
                          View
                        </a>
                      ) : (
                        "---"
                      )
                    }
                  />
                );
              })}
            </RazorTable>
          </div>

          <div className="flex items-center justify-end gap-[12px] mt-[16px]">
            <button
              type="button"
              disabled={!hasPrevious || loading_bridge}
              onClick={handlePrevious}
              className="flex items-center gap-[4px] rounded-[8px] border border-[#E4E4E7] px-[14px] py-[8px] text-[13px] font-medium text-[#3F3F46] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="size-[14px]" />
              Previous
            </button>
            <button
              type="button"
              disabled={!hasNext || loading_bridge}
              onClick={handleNext}
              className="flex items-center gap-[4px] rounded-[8px] border border-[#E4E4E7] px-[14px] py-[8px] text-[13px] font-medium text-[#3F3F46] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="size-[14px]" />
            </button>
          </div>
        </div>
      </DashboardLayoutMain>

      <CustomModal
        ref={dialogRef}
        onClose={closeExportDialog}
        headerTitle="Export Monthly Report"
        containerClass="!w-[440px]"
      >
        <section className="p-[24px] space-y-[16px]">
          <p className="text-[13px] text-[#758089]">
            Bridge exports a full month at a time — pick the year and month to
            download as a CSV report.
          </p>
          <RazorInputField
            color="black-light"
            type="select"
            label="Year"
            selectOption={YEAR_OPTIONS}
            value={exportYear}
            onChange={(param: any) => setExportYear(param)}
          />
          <RazorInputField
            color="black-light"
            type="select"
            label="Month"
            selectOption={MONTH_OPTIONS}
            value={exportMonth}
            onChange={(param: any) => setExportMonth(param)}
          />
          <div className="flex justify-end gap-[12px] pt-[8px]">
            <Button variant="ghost" onClick={closeExportDialog}>
              Cancel
            </Button>
            <Button
              disabled={!exportYear || !exportMonth}
              onClick={handleConfirmExport}
            >
              Download CSV
            </Button>
          </div>
        </section>
      </CustomModal>
    </>
  );
};

export default BridgeTransaction;
